/**
 * Cache-invalidation helpers for batch mutations.
 *
 * Any mutation that changes a batch — a lifecycle transition or an approved
 * correction — can ripple into more than the
 * surface that triggered it: input-data changes move the workpaper figures,
 * re-run validation (issues), re-render reports, and always write an audit event.
 * Invalidating just the one query that triggered the change leaves the other
 * tabs showing stale values until reload.
 *
 * `queryKeys.batches.detail(batchId)` is the common prefix for every per-batch
 * sub-resource (metadata, issues, audit, reports, calc-results, corrections),
 * so a single prefix invalidation refreshes the whole batch-detail surface.
 * We also invalidate the list so the summary row (state, versions) re-renders.
 *
 * Data-visualization series are cached aggressively (historical windows never
 * go stale — see `seriesStaleTime`), but the underlying gas-day values DO move
 * when an upload is accepted/replaced, a correction is approved, or a period is
 * backfilled. Those all funnel through here, so we also invalidate the series
 * prefix to keep the Explorer + batch Data tab in sync.
 */

import type { QueryClient } from "@tanstack/react-query";
import { queryKeys } from "./queryKeys";

export async function invalidateBatchData(
  queryClient: QueryClient,
  batchId: string,
): Promise<void> {
  await Promise.all([
    // Prefix match — covers detail metadata + issues + audit + reports +
    // calc-results + corrections for this batch.
    queryClient.invalidateQueries({ queryKey: queryKeys.batches.detail(batchId) }),
    queryClient.invalidateQueries({ queryKey: queryKeys.batches.list() }),
    // Source data moved — drop the cached daily series (incl. cache-forever
    // historical windows) so the Explorer + Data tab refetch.
    queryClient.invalidateQueries({ queryKey: queryKeys.data.seriesAll() }),
    // Corrections moved with it — the Explorer's window-scoped overlay
    // (GET /data/corrections) must reflect the new propose/approve/reject.
    queryClient.invalidateQueries({ queryKey: queryKeys.data.correctionsAll() }),
  ]);
}

/**
 * Drop everything the LCFS screens read after a period is loaded.
 *
 * One prefix covers all of it: the two rosters and the built allocation share
 * `queryKeys.lcfs.all`. That last one matters most — `useAllocationInputs`
 * caches the whole seventeen-period replay under a single key with a five-minute
 * `staleTime`, so without this the ledger keeps serving the allocation as it was
 * *before* the upload, and the load looks like it silently failed.
 *
 * Nothing invalidated any `lcfs.*` key before this, and nothing needed to: every
 * LCFS route was a GET, and a period could only be loaded by running a script
 * against the database.
 */
export async function invalidateLcfsData(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.lcfs.all }),
    queryClient.invalidateQueries({ queryKey: queryKeys.uploads.all }),
  ]);
}

/**
 * Kick off a cache refresh without blocking the UI that triggered it.
 *
 * `invalidateQueries` resolves only once every *active* matching query has
 * refetched, and `apiFetch` takes no default timeout — so awaiting one before
 * closing a modal makes the close hostage to the slowest request on the page,
 * and to a wedged one forever. That is exactly what it looked like: the action
 * had succeeded, but the modal sat there with nothing to explain itself and had
 * to be dismissed by hand.
 *
 * By the time this is called the mutation has been acknowledged and the operator
 * has been told so by a toast. The refresh is housekeeping on top of that, so it
 * belongs after the close, not in front of it.
 *
 * Errors are swallowed deliberately: a refresh that fails leaves slightly stale
 * numbers, which the next poll or navigation corrects, and a red toast landing
 * after a green one would report a successful action as a failed one.
 *
 * Takes the promise rather than a (queryClient, batchId) pair so the upload
 * paths — which refresh `uploads.all` alongside the batch surface in a single
 * `Promise.all` — reach for this same helper instead of a second idiom.
 */
export function refreshInBackground(work: Promise<unknown>): void {
  void work.catch(() => {});
}
